import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Building, Users, Loader } from "lucide-react";
import { apiClient } from "../../config/api";
import API_CONFIG from "../../config/api";
import "./mention-dropdown.scss";

/**
 * MentionDropdown Component
 * Shows a dropdown of clients when user types @ in a text input
 * 
 * Uses the /api/TagNotifications/clients endpoint which returns:
 * - For Helxon users: ALL active clients
 * - For other client users: Their OWN client + Helxon client
 *
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the dropdown is visible
 * @param {string} props.searchText - Text after @ to filter clients
 * @param {Object} props.position - Position of the dropdown { top, left }
 * @param {Function} props.onSelect - Callback when a client is selected
 * @param {Function} props.onClose - Callback to close the dropdown
 */
export default function MentionDropdown({
  isVisible,
  searchText = "",
  position = { top: 0, left: 0 },
  onSelect,
  onClose,
}) {
  const dropdownRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tagging clients from the API
  const fetchTaggingClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.TAG_NOTIFICATION_CLIENTS);
      // Map response to expected format { id, name }
      const clientList = (response.data || []).map((client) => ({
        id: client.clientUUID || client.id,
        name: client.clientName || client.name,
      }));
      setClients(clientList);
    } catch (err) {
      console.error("Failed to fetch tagging clients:", err);
      setError("Failed to load clients");
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch clients when dropdown becomes visible
  useEffect(() => {
    if (isVisible) {
      fetchTaggingClients();
    }
  }, [isVisible, fetchTaggingClients]);

  // Filter clients based on search text
  const filteredClients = useMemo(() => {
    if (!searchText) return clients;
    const search = searchText.toLowerCase();
    return clients.filter((client) =>
      client.name.toLowerCase().includes(search)
    );
  }, [clients, searchText]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredClients]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredClients.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
        case "Tab":
          e.preventDefault();
          if (filteredClients[selectedIndex]) {
            onSelect(filteredClients[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, filteredClients, selectedIndex, onSelect, onClose]);

  // Handle click outside to close
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      ref={dropdownRef}
      className="mention-dropdown"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className="mention-dropdown__header">
        <Users size={14} />
        <span>Tag a Client</span>
      </div>

      <div className="mention-dropdown__list">
        {loading ? (
          <div className="mention-dropdown__loading">
            <Loader size={16} className="spinning" />
            <span>Loading clients...</span>
          </div>
        ) : error ? (
          <div className="mention-dropdown__error">
            <span>{error}</span>
            <button onClick={fetchTaggingClients} className="mention-dropdown__retry">
              Retry
            </button>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="mention-dropdown__empty">
            {searchText ? `No clients found for "${searchText}"` : "No clients available"}
          </div>
        ) : (
          filteredClients.map((client, index) => (
            <button
              key={client.id}
              className={`mention-dropdown__item ${
                index === selectedIndex ? "mention-dropdown__item--selected" : ""
              }`}
              onClick={() => onSelect(client)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="mention-dropdown__item-icon">
                <Building size={14} />
              </div>
              <span className="mention-dropdown__item-name">{client.name}</span>
            </button>
          ))
        )}
      </div>

      <div className="mention-dropdown__hint">
        <span>↑↓ to navigate</span>
        <span>↵ to select</span>
        <span>esc to close</span>
      </div>
    </div>
  );
}
